/*
 * CodeBlock: commands and config in the dark terminal look of the landing films. Mono, always LTR, with a
 * copy button (copies the code without prompts) and optional tabs for OS variants.
 *   <CodeBlock code={`curl -fsSL https://apt.bpanel.dev/install.sh | sudo bash`} prompt />
 *   <CodeBlock tabs={[{ id: 'debian-12', label: 'Debian 12', code: … }, …]} value={os} onValueChange={setOs} />
 */
import { Check, Copy } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { useCopy } from '@/lib/useCopy'

export type CodeTab = { id: string; label: string; code: string }

export function CodeBlock({
  code,
  tabs,
  value,
  onValueChange,
  title,
  prompt,
  className,
  wrap,
}: {
  code?: string
  tabs?: CodeTab[]
  value?: string
  onValueChange?: (id: string) => void
  /** shown in the title bar when there are no tabs, e.g. "root@server1" */
  title?: string
  /** prefix each line with `$` (not copied) */
  prompt?: boolean
  className?: string
  wrap?: boolean
}) {
  const { t } = useTranslation('common')
  const [copied, copy] = useCopy()
  const [internal, setInternal] = useState(tabs?.[0]?.id)
  const active = value ?? internal
  const tab = tabs?.find((x) => x.id === active) ?? tabs?.[0]
  const text = tab?.code ?? code ?? ''
  const select = (id: string) => {
    setInternal(id)
    onValueChange?.(id)
  }
  return (
    <div
      dir="ltr"
      className={cn(
        'overflow-hidden rounded-2xl bg-terminal text-start shadow-card ring-1 ring-terminal-line',
        className,
      )}
    >
      <div className="flex min-h-11 items-center gap-1 border-b border-terminal-line ps-3 pe-1.5">
        <span className="me-2 flex gap-1.5" aria-hidden="true">
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="size-2.5 rounded-full bg-white/15" />
        </span>
        {tabs ? (
          <div role="tablist" className="scrollbar-none flex min-w-0 flex-1 gap-0.5 overflow-x-auto">
            {tabs.map((x) => (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={x.id === tab?.id}
                onClick={() => select(x.id)}
                className={cn(
                  'h-8 shrink-0 rounded-lg px-2.5 font-mono text-12 transition-colors',
                  x.id === tab?.id ? 'bg-white/10 text-white' : 'text-terminal-muted hover:text-white',
                )}
              >
                {x.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="min-w-0 flex-1 truncate font-mono text-12 text-terminal-muted">{title}</span>
        )}
        <button
          type="button"
          onClick={() => copy(text)}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-12 font-medium text-terminal-muted transition-colors hover:bg-white/10 hover:text-white"
        >
          {copied ? (
            <Check className="size-4 text-teal-400" aria-hidden="true" />
          ) : (
            <Copy className="size-4" aria-hidden="true" />
          )}
          <span>{copied ? t('actions.copied') : t('actions.copy')}</span>
        </button>
      </div>
      <pre
        className={cn(
          'scrollbar-thin overflow-x-auto px-4 py-3.5 font-mono text-13 leading-6 text-terminal-text',
          wrap && 'break-all whitespace-pre-wrap',
        )}
      >
        <code>
          {text.split('\n').map((line, i) => (
            <span key={i} className="block">
              {prompt && line.trim() && !line.trimStart().startsWith('#') ? (
                <span className="me-2 text-teal-400 select-none" aria-hidden="true">
                  $
                </span>
              ) : null}
              <span className={line.trimStart().startsWith('#') ? 'text-terminal-muted' : undefined}>
                {line || ' '}
              </span>
            </span>
          ))}
        </code>
      </pre>
    </div>
  )
}
